import { useCallback, useEffect, useState } from 'react'
import { Button } from '../ui/Button'
import { Card, CardHeader } from '../ui/Card'
import { Modal } from '../ui/Modal'
import {
  setupProgressAdminApi,
  type GoLiveReadiness,
} from '../../services/setupProgressAdminApi'

type AdminTakeLivePanelProps = {
  storeId: string | number
  onLive?: () => void
}

export function AdminTakeLivePanel({ storeId, onLive }: AdminTakeLivePanelProps) {
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [readiness, setReadiness] = useState<GoLiveReadiness | null>(null)
  const [forceOpen, setForceOpen] = useState(false)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await setupProgressAdminApi.getGoLiveReadiness(storeId)
      setReadiness(data)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load go-live readiness')
    } finally {
      setLoading(false)
    }
  }, [storeId])

  useEffect(() => {
    void refresh()
  }, [refresh])

  async function takeLive(force: boolean) {
    setSubmitting(true)
    setError(null)
    try {
      const data = await setupProgressAdminApi.postGoLive(storeId, force)
      setReadiness(data)
      setForceOpen(false)
      onLive?.()
      await refresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to take store live')
      await refresh()
    } finally {
      setSubmitting(false)
    }
  }

  if (loading && !readiness) {
    return (
      <Card>
        <CardHeader title="Go live" description="Loading readiness…" />
      </Card>
    )
  }

  if (!readiness) {
    return error ? (
      <Card>
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      </Card>
    ) : null
  }

  if (readiness.alreadyLive) {
    return (
      <Card>
        <CardHeader
          title="Live"
          description="Organisation status is active — the customer storefront is available."
        />
      </Card>
    )
  }

  const canForce = readiness.blockers.some((b) => b.overridable) && !readiness.blockers.some((b) => !b.overridable)

  return (
    <>
      <Card>
        <CardHeader
          title="Take live"
          description="Activate the organisation when setup is ready, or override incomplete steps if needed."
          action={
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                disabled={submitting || !readiness.ready}
                onClick={() => void takeLive(false)}
              >
                {submitting ? 'Activating…' : 'Take live'}
              </Button>
              {canForce && !readiness.ready ? (
                <Button
                  type="button"
                  variant="secondary"
                  disabled={submitting}
                  onClick={() => setForceOpen(true)}
                >
                  Take live anyway
                </Button>
              ) : null}
            </div>
          }
        />
        <div className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            Setup: {readiness.completedRequiredCount}/{readiness.requiredTotalCount} required · Org
            status: {readiness.organisationStatus} · Platform: {readiness.storePlatform}
          </p>
          {readiness.blockers.length > 0 ? (
            <ul className="space-y-2">
              {readiness.blockers.map((b) => (
                <li
                  key={b.code}
                  className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-amber-950 dark:text-amber-100"
                >
                  {b.message}
                  {b.overridable ? (
                    <span className="mt-0.5 block text-xs opacity-80">Platform can override</span>
                  ) : (
                    <span className="mt-0.5 block text-xs opacity-80">Cannot override</span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-emerald-700 dark:text-emerald-300">Ready to go live.</p>
          )}
          {error ? (
            <p className="text-destructive" role="alert">
              {error}
            </p>
          ) : null}
        </div>
      </Card>

      <Modal
        open={forceOpen}
        onClose={() => !submitting && setForceOpen(false)}
        title="Take live anyway?"
        size="md"
        footer={
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="ghost" disabled={submitting} onClick={() => setForceOpen(false)}>
              Cancel
            </Button>
            <Button type="button" disabled={submitting} onClick={() => void takeLive(true)}>
              {submitting ? 'Activating…' : 'Confirm go live'}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-muted-foreground">
          Setup or billing is incomplete. Activating will make the customer storefront available
          anyway. Suspended organisations cannot be activated this way.
        </p>
        {readiness.blockers.length > 0 ? (
          <ul className="mt-3 list-inside list-disc space-y-1 text-sm text-muted-foreground">
            {readiness.blockers.map((b) => (
              <li key={b.code}>{b.message}</li>
            ))}
          </ul>
        ) : null}
      </Modal>
    </>
  )
}
